import React from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  type?: 'danger' | 'warning' | 'info';
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
  type = 'danger',
}) => {
  if (!isOpen) return null;

  const typeConfig = {
    danger: {
      bgIcon: 'bg-rose-50 border-rose-100 text-rose-600',
      btnClass: 'bg-rose-600 hover:bg-rose-700 shadow-rose-200 focus:ring-rose-500/20 text-white',
    },
    warning: {
      bgIcon: 'bg-amber-50 border-amber-100 text-amber-600',
      btnClass: 'bg-amber-500 hover:bg-amber-600 shadow-amber-200 focus:ring-amber-500/20 text-white',
    },
    info: {
      bgIcon: 'bg-brand-surface border-brand-border text-brand-primary',
      btnClass: 'bg-brand-primary hover:bg-brand-hover shadow-brand-glow focus:ring-brand-primary/20 text-white',
    },
  };

  const config = typeConfig[type];

  return (
    <div className="fixed inset-0 z-[10000] flex justify-center items-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-ink/40 backdrop-blur-md animate-fade-in" 
        onClick={loading ? undefined : onClose} 
      />

      {/* Modal card */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-page-border p-6 flex flex-col gap-4 animate-fade-scale z-10">
        
        {/* Header / Close */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 text-ink-faint hover:text-ink p-1 hover:bg-page-surface rounded-lg transition-colors disabled:opacity-50"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="flex gap-4 items-start pr-6 mt-1">
          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${config.bgIcon}`}>
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="space-y-1.5 flex-1 min-w-0">
            <h3 className="font-heading font-bold text-lg text-ink leading-tight">
              {title}
            </h3>
            <p className="text-xs text-ink-muted leading-relaxed">
              {message}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end mt-2 pt-3 border-t border-page-border">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 bg-page-surface text-ink-muted rounded-xl hover:bg-page-panel transition-colors text-xs font-semibold border border-page-border disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-xl transition-all text-xs font-bold flex items-center gap-1.5 shadow-sm disabled:opacity-50 ${config.btnClass}`}
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
